import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const AuthContext = createContext(null);
const TOKEN_KEY = 'mtg_id_token';
const API = process.env.REACT_APP_API_URL ?? '';

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [user, setUser] = useState(null);          // { email, role } when signed in + authorized
  const [authError, setAuthError] = useState(null); // 'Not whitelisted' etc.
  const [loading, setLoading] = useState(true);

  const login = useCallback((newToken) => {
    localStorage.setItem(TOKEN_KEY, newToken);
    setAuthError(null);
    setToken(newToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setUser(null);
    setAuthError(null);
  }, []);

  // Validate the stored token on mount / token change
  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${API}/api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          setUser(data);
          setAuthError(null);
        } else if (res.status === 403) {
          // Authenticated with Google but not on the whitelist
          const data = await res.json().catch(() => ({}));
          setAuthError(data.error || 'Access denied');
          setUser(null);
        } else {
          // Bad / expired token — force re-login
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
          setAuthError(null);
        }
      } catch (e) {
        if (!cancelled) setAuthError('Could not reach server');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Authenticated fetch: automatically attaches the bearer token,
  // and logs the user out on 401 so the login screen reappears.
  const authFetch = useCallback(async (path, options = {}) => {
    const headers = { ...(options.headers || {}) };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API}${path}`, { ...options, headers });
    if (res.status === 401) {
      logout();
    }
    return res;
  }, [token, logout]);

  return (
    <AuthContext.Provider value={{ token, user, authError, loading, login, logout, authFetch }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
